/**
 * creature/templates/cat.ts — The First Companion
 *
 * The house cat template: small, warm, independent. Pointed ears,
 * gold eyes, a tail that curls when content. This is the generalized
 * form of the title.ts cat skeleton — same proportions, same charm,
 * but now expressed as pure data that any renderer can consume.
 *
 * The cat sleeps by the fire. It grooms itself at 30 seconds.
 * It yawns at 90. It walks at 120. It was here before you arrived.
 */

import type { SkeletonDef } from "../skeleton.js";
import type { SpeciesTemplate, GaitPattern } from "../species.js";
import { registerSpecies } from "../species.js";

// ─── Cat Skeleton ───────────────────────────────────────────────────────────

const CAT_SKELETON: SkeletonDef = {
  id: "skeleton_cat",
  rootJointId: "hip",
  joints: [
    // Root
    {
      id: "hip",
      parentId: null,
      restOffset: { x: 0, y: 0 },
      restAngle: -0.45,
      minAngle: -1.5,
      maxAngle: 0.2,
      length: 10,      // spine length to shoulder (world units, scaled at render)
      tags: ["root", "hip"],
    },
    // Spine -> Shoulder
    {
      id: "shoulder",
      parentId: "hip",
      restOffset: { x: 0, y: 0 },
      restAngle: 0,
      minAngle: -0.3,
      maxAngle: 0.3,
      length: 5,        // neck length to head
      tags: ["shoulder", "spine"],
    },
    // Neck -> Head
    {
      id: "neck",
      parentId: "shoulder",
      restOffset: { x: 0, y: 0 },
      restAngle: -0.3,
      minAngle: -1.2,
      maxAngle: 0.3,
      length: 3.2,      // head radius
      tags: ["neck"],
    },
    // Head (end effector for look-at)
    {
      id: "head",
      parentId: "neck",
      restOffset: { x: 0, y: 0 },
      restAngle: 0,
      minAngle: -0.5,
      maxAngle: 0.5,
      length: 0,
      tags: ["head"],
    },
    // Tail segments
    {
      id: "tail_0",
      parentId: "hip",
      restOffset: { x: 5, y: 1 },
      restAngle: -0.3,
      minAngle: -1.5,
      maxAngle: 1.5,
      length: 5,
      tags: ["tail"],
    },
    {
      id: "tail_1",
      parentId: "tail_0",
      restOffset: { x: 0, y: 0 },
      restAngle: -0.4,
      minAngle: -1.5,
      maxAngle: 1.5,
      length: 4,
      tags: ["tail"],
    },
    {
      id: "tail_2",
      parentId: "tail_1",
      restOffset: { x: 0, y: 0 },
      restAngle: -0.5,
      minAngle: -2.0,
      maxAngle: 2.0,
      length: 3,
      tags: ["tail", "tail_tip"],
    },
    // Ear joints (for twitch animation)
    {
      id: "ear_l",
      parentId: "head",
      restOffset: { x: -1.8, y: -1.5 },
      restAngle: -1.3,
      minAngle: -2.0,
      maxAngle: -0.5,
      length: 3.5,
      tags: ["ear", "ear_l"],
    },
    {
      id: "ear_r",
      parentId: "head",
      restOffset: { x: 1.5, y: -1.8 },
      restAngle: -1.3,
      minAngle: -2.0,
      maxAngle: -0.5,
      length: 3.5,
      tags: ["ear", "ear_r"],
    },
  ],

  drawOrder: [
    { name: "back_legs",   joints: ["hip"] },
    { name: "tail",        joints: ["hip", "tail_0", "tail_1", "tail_2"] },
    { name: "body",        joints: ["hip", "shoulder"] },
    { name: "scratch_leg", joints: ["hip", "head"] },
    { name: "front_legs",  joints: ["shoulder"] },
    { name: "head",        joints: ["head", "neck", "ear_l", "ear_r"] },
    { name: "fire_glow",   joints: ["hip", "shoulder"] },
  ],

  ikChains: [
    {
      id: "look_at",
      jointIds: ["shoulder", "neck", "head"],
      targetMode: "point",
      iterations: 3,
      tolerance: 0.5,
    },
  ],
};

// ─── Cat Gaits ──────────────────────────────────────────────────────────────

const CAT_WALK: GaitPattern = {
  name: "walk",
  speedRange: [0, 2],
  legPhaseOffsets: {
    leg_front_l: 0,
    leg_front_r: Math.PI,
    leg_back_l: Math.PI * 0.5,
    leg_back_r: Math.PI * 1.5,
  },
  strideLength: 0.36,
  bodyBob: 0.3,
  bodyRoll: 0.1,
  headBob: 0.15,
};

const CAT_TROT: GaitPattern = {
  name: "trot",
  speedRange: [2, 4],
  legPhaseOffsets: {
    leg_front_l: 0,
    leg_front_r: Math.PI,
    leg_back_l: Math.PI,       // diagonal pair
    leg_back_r: 0,
  },
  strideLength: 0.6,
  bodyBob: 0.5,
  bodyRoll: 0.15,
  headBob: 0.2,
};

// ─── Cat Species Template ───────────────────────────────────────────────────

export const SPECIES_CAT: SpeciesTemplate = {
  id: "cat_house",
  displayName: "House Cat",
  bodyPlan: "quadruped",
  skeleton: CAT_SKELETON,
  size: "small",
  baseSpeed: 1.5,
  heightAtShoulder: 0.4,
  gaits: [CAT_WALK, CAT_TROT],
  turnRate: Math.PI * 2,  // cats turn fast
  palette: {
    body: "#353530",      // dark charcoal (matches title.ts P.cat)
    markings: "#404038",
    eyes: "#d0c040",      // gold (matches title.ts)
    nose: "#c88282",
    accent: "#323238",    // paw pads
    belly: "#3a3a34",
  },
  bodyShape: {
    bodyLength: 10,
    bodyWidth: 3.5,
    headRadius: 3.2,
    tailSegments: 3,
    tailLength: 12,
    earStyle: "pointed",
    earSize: 1.0,
    legThickness: 1.5,
    hasMane: false,
    hasWings: false,
    neckLength: 1.0,
    snoutLength: 0.2,
    legLength: 1.0,
  },
  behaviorDefaults: {
    energy: 0.3,           // cats are chill
    sociability: 0.4,      // selectively social
    curiosity: 0.7,        // very curious
    obedience: 0.15,       // independent AF
    playfulness: 0.5,
    courage: 0.4,
    agility: 0.9,          // extremely agile
    stamina: 0.3,          // short bursts
    nocturnality: 0.6,     // crepuscular
    territoriality: 0.6,
    packInstinct: 0.1,     // solitary
  },
  rideable: false,
  riderAttachJoint: "",
  riderOffsetZ: 0,
  saddleable: false,
  sounds: {
    idle: ["purr"],
    happy: ["purr_loud", "chirp"],
    hungry: ["meow", "meow_insistent"],
    walk: ["paw_pad_soft"],
    run: ["paw_pad_rapid"],
    special: ["meow", "hiss", "chirp", "trill"],
  },
  moodContribution: {
    warmth: 0.05,
    coziness: 0.1,
    energy: 0.02,
    nature: 0.03,
  },
};

// ─── Breed Variants ─────────────────────────────────────────────────────────

import { createBreed } from "../species.js";

export const SPECIES_CAT_ORANGE = createBreed(SPECIES_CAT, {
  id: "cat_orange_tabby",
  displayName: "Orange Tabby",
  palette: {
    ...SPECIES_CAT.palette,
    body: "#c87830",
    markings: "#a06020",
    belly: "#d89848",
  },
  behaviorDefaults: {
    ...SPECIES_CAT.behaviorDefaults,
    sociability: 0.7,     // orange tabbies are notoriously friendly
    energy: 0.5,
    playfulness: 0.7,
  },
});

export const SPECIES_CAT_SIAMESE = createBreed(SPECIES_CAT, {
  id: "cat_siamese",
  displayName: "Siamese",
  palette: {
    ...SPECIES_CAT.palette,
    body: "#e8d8c0",
    markings: "#5a3a2a",
    eyes: "#4080c0",      // blue eyes
    nose: "#6a4a3a",
    belly: "#f0e8d8",
  },
  bodyShape: {
    ...SPECIES_CAT.bodyShape,
    bodyWidth: 3.0,       // more slender
    earSize: 1.3,         // larger ears
    snoutLength: 0.35,
    legLength: 1.15,      // longer legs
  },
  behaviorDefaults: {
    ...SPECIES_CAT.behaviorDefaults,
    energy: 0.6,
    sociability: 0.8,     // very vocal and social
    curiosity: 0.9,
  },
});

export const SPECIES_CAT_BLACK = createBreed(SPECIES_CAT, {
  id: "cat_black",
  displayName: "Black Cat",
  palette: {
    ...SPECIES_CAT.palette,
    body: "#1a1a1e",
    markings: "#222226",
    eyes: "#40c040",      // green eyes
    belly: "#222226",
    accent: "#1e1e22",
  },
  behaviorDefaults: {
    ...SPECIES_CAT.behaviorDefaults,
    curiosity: 0.85,
    courage: 0.6,
    nocturnality: 0.8,
  },
});

// ─── Register All Cat Species ───────────────────────────────────────────────

export function registerCats(): void {
  registerSpecies(SPECIES_CAT);
  registerSpecies(SPECIES_CAT_ORANGE);
  registerSpecies(SPECIES_CAT_SIAMESE);
  registerSpecies(SPECIES_CAT_BLACK);
}
